/**
 * text-arc — drop-in addon for veyo-product-personalizer.
 *
 * Replaces the legacy jQuery + jQuery UI text-arc plugin (TS_Addons/text-arc).
 * Registers two extensions on window.Veyop:
 *
 *   1. A text effect panel shown by the host Effects UI.
 *   2. An item renderer that swaps plain HTML text for inline curved SVG when
 *      textCurve != 0.
 */
import ArcPanel from './ArcPanel.vue';
import { curvedTextStyleSupport, exportCurvedText, isCurvedText, measureCurvedText, prepareCurvedText, renderCurvedText, type CurvedTextItem } from './outlineArc';
import { t } from './i18n';

interface MinimalItem {
  type?: string;
  textCurve?: unknown;
  [key: string]: unknown;
}

interface VeyopFacade {
  hooks?: { on: (e: string, h: (p: unknown) => void) => void };
  itemRenderers?: { register: (r: unknown) => unknown };
  panels?: { register: (p: unknown) => unknown };
  itemBounds?: { register: (r: unknown) => unknown };
  imagePipeline?: { registerItemSvgExporter?: (r: unknown) => () => void };
  textStyles?: { register: (r: unknown) => unknown };
}

let registered = false;

function registerOnVeyop(Veyop: VeyopFacade): void {
  if (registered || !Veyop.itemRenderers?.register || !Veyop.panels?.register || !Veyop.itemBounds?.register) return;
  registered = true;
  if (Veyop.itemRenderers?.register) {
    Veyop.itemRenderers.register({
      id: 'text-arc',
      priority: 50,
      matches: (item: MinimalItem) => item?.type === 'text' && Number(item?.textCurve ?? 0) !== 0,
      render: renderCurvedText,
    });
  }
  if (Veyop.itemBounds?.register) {
    Veyop.itemBounds.register({
      id: 'text-arc',
      priority: 50,
      matches: (item: MinimalItem) => item?.type === 'text' && Number(item?.textCurve ?? 0) !== 0,
      measure: measureCurvedText,
      // Curved text has no separate scale field — its geometry is driven entirely by fontSize,
      // so a dragged box is recorded by scaling the font uniformly. Without this the host would
      // keep the box and the next edit would snap it back to the natural size.
      fitToBox: (item: MinimalItem, box: { width: number; height: number }) => {
        const bounds = measureCurvedText(item as CurvedTextItem);
        if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
        const current = Number(item.fontSize);
        if (!Number.isFinite(current) || current <= 0) return null;
        const scale = Math.min(box.width / bounds.width, box.height / bounds.height);
        if (!Number.isFinite(scale) || scale <= 0) return null;
        const next = Math.max(8, Math.min(300, Math.round(current * scale)));
        return next === Math.round(current) ? null : { fontSize: next };
      },
    });
  }
  if (Veyop.panels?.register) {
    Veyop.panels.register({
      id: 'text-arc',
      label: t('Text Curve'),
      placement: 'text-effects',
      description: t('Curve text around a path'),
      priority: 50,
      preview: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path id="vta-arc" d="M7 27 A14 14 0 0 1 33 27" fill="none" stroke="currentColor" stroke-opacity=".25" stroke-width="1.5"/><text font-family="Arial, sans-serif" font-size="8" font-weight="700" fill="currentColor"><textPath href="#vta-arc" startOffset="50%" text-anchor="middle">ARC</textPath></text></svg>',
      isActive: (item: MinimalItem | null) => Math.abs(Number((item as { textCurve?: unknown } | null)?.textCurve ?? 0)) > 0,
      matches: (item: MinimalItem | null) => item?.type === 'text',
      render: (ctx: { item: unknown; update: (patch: Record<string, unknown>) => void }) => ({
        component: ArcPanel,
        props: { item: ctx.item, update: ctx.update },
      }),
    });
  }
  // Typography Pro (priority 30) owns layers it styles; plain curved text is ours.
  Veyop.textStyles?.register({
    id: 'text-arc',
    priority: 50,
    matches: (item: MinimalItem) => isCurvedText(item as CurvedTextItem) && !(item as CurvedTextItem).typography,
    supports: (item: MinimalItem) => curvedTextStyleSupport(item as CurvedTextItem),
  });
  const registerExporter = Veyop.imagePipeline?.registerItemSvgExporter;
  if (typeof registerExporter === 'function') {
    registerExporter({
      id: 'text-arc',
      priority: 50,
      matches: (item: MinimalItem) => item?.type === 'text' && Number(item?.textCurve ?? 0) !== 0,
      // Outlines need no fonts. Only if the face cannot be parsed does the export fall back to
      // `<text>` markup, which a data: URL image cannot draw without the inlined face CSS.
      needsFontFaceCss: true,
      prepare: (item: MinimalItem) => prepareCurvedText(item as CurvedTextItem),
      exportSvg: (item: MinimalItem, ctx?: { fontFaceCss?: string }) => exportCurvedText(item as CurvedTextItem, ctx),
    });
  }
}

function boot(): void {
  const w = window as unknown as { Veyop?: VeyopFacade };

  // Prefer lifecycle event if hooks are already available.
  // This ensures we register after the designer SPA is mounted.
  const tryRegister = () => {
    const v = w.Veyop;
    if (v && (v.itemRenderers || v.panels || v.itemBounds)) {
      registerOnVeyop(v);
    }
  };

  if (w.Veyop?.hooks?.on) {
    w.Veyop.hooks.on('veyop:designer:ready', tryRegister);
  }

  // Also attempt immediate registration (covers cases where ready already fired)
  // and keep a short polling fallback for older builds.
  tryRegister();

  const start = Date.now();
  const tick = () => {
    const v = w.Veyop;
    if (v && (v.itemRenderers || v.panels || v.itemBounds)) {
      registerOnVeyop(v);
      return;
    }
    if (Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
